import { z } from 'zod';
import { emailRegExp, phoneRegExp } from '@/lib/utils/validation';
import { IAdditionalEmailCredentials } from '@/entities/admin-app/user';
import { TFunction } from 'i18next';
import { IPasswordSecurity } from '@/entities/admin-app/filials';
import { usePasswordSchema } from '@hooks/password/useGeneratePolicy';

export const validateSchemeStatus = (t: TFunction) =>
  z.number({
    invalid_type_error: t('validation.selectUserStatus'),
    required_error: t('validation.statusRequired')
  });

export const validateSchemeCustomer = (t: TFunction) =>
  z.object({
    Id: z
      .number({
        invalid_type_error: t('validation.selectDepartment'),
        required_error: t('validation.divisionRequired')
      })
      .positive({
        message: t('validation.selectDepartment')
      })
  });

export const validateSchemePositions = (t: TFunction) =>
  z
    .array(
      z.object({
        Position: z.object({
          Name: z
            .string()
            .max(64, {
              message: t('validation.maxLengthSymbolsWithCount.symbols', { count: 64 })
            })
            .refine((data) => /\S/.test(data), {
              message: t('validation.enterPosition')
            })
        })
      })
    )
    .nullable();

export const validateSchemeDataStorageQuota = (t: TFunction) =>
  z.object({
    Id: z
      .number({
        invalid_type_error: t('validation.selectQuota'),
        required_error: t('validation.quotaMandatory')
      })
      .optional()
      .nullable()
  });

function validateAdditionalEmailNotEqual(data?: IAdditionalEmailCredentials) {
  if (!data?.Email || !data?.AdditionalEmail) {
    return true;
  }
  return data?.AdditionalEmail !== data?.Email;
}
export const validateAdditionalEmailScheme = (t: TFunction) =>
  z
    .object({
      AdditionalEmail: z
        .string()
        .regex(emailRegExp, { message: t('validation.wrongEmailFormat') })
        .or(z.literal(''))
        .optional()
        .nullable(),
      Email: z.string().or(z.literal('')).optional().nullable()
    })
    .refine(validateAdditionalEmailNotEqual, {
      message: t('validation.emailsShouldNotBeTheSame'),
      path: ['AdditionalEmail']
    });
export const validateLoginScheme = (t: TFunction) =>
  z.object({
    Login: z
      .string()
      .min(1, { message: t('validation.enterLogin') })
      .max(150, { message: t('validation.maxLengthSymbolsWithCount.symbols', { count: 150 }) })
      .refine((data) => !/\s/.test(data), {
        message: t('validation.loginWithoutSpaces')
      })
  });

export const isSaasScheme = (t: TFunction) =>
  z.object({
    isSaaS: z.literal(true),
    Email: z
      .string()
      .regex(emailRegExp, { message: t('validation.wrongEmailFormat') })
      .min(1, { message: t('validation.enterEmail') })
      .max(255, { message: t('validation.maxLengthSymbolsWithCount.symbols', { count: 255 }) })
  });

export const noSaasScheme = (t: TFunction) =>
  z.object({
    isSaaS: z.literal(false),
    Email: z
      .string()
      .regex(emailRegExp, { message: t('validation.wrongEmailFormat') })
      .max(255, { message: t('validation.maxLengthSymbolsWithCount.symbols', { count: 255 }) })
      .or(z.literal(''))
      .optional()
      .nullable()
  });

export const validateCreateCredentialsNoSaas = (
  t: TFunction,
  passwordRules: IPasswordSecurity,
  isPasswordEnabled: boolean
) => {
  const passwordRulesZ = usePasswordSchema(passwordRules, t);

  return z
    .object({
      isSaas: z.literal(false),
      Email: z
        .string()
        .max(255, { message: t('validation.maxLengthSymbolsWithCount.symbols', { count: 255 }) })
        .regex(emailRegExp, { message: t('validation.wrongEmailFormat') })
        .or(z.literal(''))
        .optional()
        .nullable(),
      AdditionalEmail: z
        .string()
        .regex(emailRegExp, { message: t('validation.wrongEmailFormat') })
        .or(z.literal(''))
        .optional()
        .nullable(),
      Login: z
        .string()
        .min(1, { message: t('validation.enterLogin') })
        .max(150, { message: t('validation.maxLengthSymbolsWithCount.symbols', { count: 150 }) })
        .refine((data) => !/\s/.test(data), {
          message: t('validation.loginWithoutSpaces')
        }),
      Password: isPasswordEnabled ? passwordRulesZ : z.string().nullable().default('')
    })
    .refine(
      (data) =>
        (data.Email === '' && data.AdditionalEmail === '') || data.Email !== data.AdditionalEmail,
      {
        message: t('validation.emailsShouldNotBeTheSame'),
        path: ['AdditionalEmail']
      }
    );
};

export const validateCreateCredentialsSaas = (
  t: TFunction,
  passwordRules: IPasswordSecurity,
  isPasswordEnabled: boolean
) => {
  const passwordRulesZ = usePasswordSchema(passwordRules, t);

  return z
    .object({
      isSaas: z.literal(true),
      Email: z
        .string()
        .regex(emailRegExp, { message: t('validation.wrongEmailFormat') })
        .min(1, { message: 'Введите email' })
        .max(255, { message: t('validation.maxLengthSymbolsWithCount.symbols', { count: 255 }) }),
      AdditionalEmail: z
        .string()
        .regex(emailRegExp, { message: t('validation.wrongEmailFormat') })
        .or(z.literal(''))
        .optional()
        .nullable(),
      Login: z.string().optional().nullable(),
      Password: isPasswordEnabled ? passwordRulesZ : z.string().nullable().default('')
    })
    .refine(
      (data) =>
        (data.Email === '' && data.AdditionalEmail === '') || data.Email !== data.AdditionalEmail,
      {
        message: t('validation.emailsShouldNotBeTheSame'),
        path: ['AdditionalEmail']
      }
    );
};

export const validateSchemeCreateCredentials = (
  t: TFunction,
  passwordRules: IPasswordSecurity,
  isPasswordEnabled: boolean
) => {
  return z.union([
    validateCreateCredentialsNoSaas(t, passwordRules, isPasswordEnabled),
    validateCreateCredentialsSaas(t, passwordRules, isPasswordEnabled)
  ]);
};

export const validateSchemeEditUserInfo = (t: TFunction) =>
  z.object({
    SurName: z
      .string()
      .max(85, { message: t('validation.maxLengthSymbolsWithCount.symbols', { count: 85 }) })
      .refine((data) => /\S/.test(data), {
        message: t('validation.enterLastName')
      }),
    FirstName: z
      .string()
      .max(85, { message: t('validation.maxLengthSymbolsWithCount.symbols', { count: 85 }) })
      .refine((data) => /\S/.test(data), {
        message: t('validation.enterFirstName')
      }),
    MiddleName: z
      .string()
      .max(85, { message: t('validation.maxLengthSymbolsWithCount.symbols', { count: 85 }) })
      .optional()
      .or(z.literal(''))
      .nullable(),
    Gender: z
      .number()
      .min(1, { message: t('validation.chooseGender') })
      .or(z.number().default(0))
      .optional(),
    BirthDate: z
      .preprocess(
        (arg) => (typeof arg === 'string' ? new Date(arg) : ' '),
        z
          .date({ invalid_type_error: t('validation.indicateBirthday') })
          .min(new Date('1900-01-01'), { message: t('validation.enteredDateSmall') })
          .max(new Date(), { message: t('validation.dateLessCurrent') })
          .or(z.string().optional())
          .nullable()
      )
      .nullable(),
    Address: z
      .string()
      .max(255, { message: t('validation.maxLengthSymbolsWithCount.symbols', { count: 255 }) })
      .optional()
      .nullable(),
    Phone: z
      .string()
      .regex(phoneRegExp, { message: t('validation.wrongMobilePhoneFormat') })
      .or(z.literal(''))
      .optional()
      .nullable(),
    AdditionalPhone: z
      .string()
      .regex(/^[0-9]*$/, { message: t('validation.extensionPhoneContainNumbers') })
      .or(z.literal(''))
      .optional()
      .nullable()
  });
